import { Users } from 'lucide-react';
import { Link } from 'react-router';
import { AudiencePicker } from '@/components/campaigns/audience-picker';
import { CampaignFormSectionCard } from '@/components/campaigns/campaign-form-section-card';
import { TemplateString } from '@/components/template-string';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Separator } from '../ui/separator';

export interface CampaignAudienceSectionProps {
  tenantSlug: string;
  audienceId: string | null;
  suppressRecentNotifications: boolean;
  locale: Locales;
  audienceError?: string;
  onAudienceChange: (audienceId: string | null) => void;
  onAudienceBlur: () => void;
  onSuppressChange: (checked: boolean) => void;
  className?: string;
}

export function CampaignAudienceSection({
  tenantSlug,
  audienceId,
  suppressRecentNotifications,
  locale,
  audienceError,
  onAudienceChange,
  onAudienceBlur,
  onSuppressChange,
  className,
}: CampaignAudienceSectionProps) {
  const { LL } = useI18nContext();

  return (
    <CampaignFormSectionCard
      icon={Users}
      title={LL.createCampaign.sections.audience.title()}
      description={LL.createCampaign.sections.audience.description()}
      className={className}
    >
      <div className="space-y-4">
        <AudiencePicker
          tenantSlug={tenantSlug}
          value={audienceId}
          onChange={onAudienceChange}
          onBlur={onAudienceBlur}
          error={audienceError}
          locale={locale}
        />
        <Separator />

        <div>
          <div className="flex items-center justify-start gap-4">
            <Label
              htmlFor="suppressRecentNotifications"
              className="text-sm font-normal leading-snug"
            >
              {LL.createCampaign.sections.audience.suppressionLabel()}
            </Label>
            <Switch
              id="suppressRecentNotifications"
              checked={suppressRecentNotifications}
              onCheckedChange={onSuppressChange}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            <TemplateString
              templateString={LL.createCampaign.sections.audience.suppressionHelper()}
              componentMap={{
                SettingsLink: () => (
                  <Link
                    to={`/${tenantSlug}/settings`}
                    className="text-primary underline underline-offset-4"
                  >
                    {LL.createCampaign.sections.audience.settingsLink()}
                  </Link>
                ),
              }}
            />
          </p>
        </div>
      </div>
    </CampaignFormSectionCard>
  );
}
